import React from 'react'
import { observer } from 'mobx-react-lite'
import Section from 'app/components/structure/Section'
import { CollectibleProgress } from 'app/blocks/collectible-progress'
import { useStores } from 'app/store'
import { useDidFinishSSR } from '@mythical/ui'
import { CompactCollectibleProgress } from '../my-collectibles/CompactCollectibleProgress'
import { gql } from 'graphql-request'
import useQuery from 'app/hooks/use-query'
import Spinner from 'app/components/primitive/Spinner'

const collectibleProgressQuery = gql`
  query getCollectibleProgress($collectibleId: String!) {
    userCollectibles(collectibleId: $collectibleId) {
      isSyncing
      isEligibleForCollectibles
      currentQuarter
      paymentsThisQuarter
      paymentsNeeded
      planType
      collectibles {
        id
        title
        description
        quarter
        status
        isPurchasable
        image
        redeemedDate
        unlockDate
        shopifyUrl
        availableFrom
        availableUntil
      }
    }
  }
`

const mapCollectibleData = (data: any) => {
  const collectiblesData = data?.userCollectibles
  if (!collectiblesData) return null

  return {
    isSyncing: collectiblesData.isSyncing || false,
    isEligibleForCollectibles: collectiblesData.isEligibleForCollectibles || false,
    collectible: collectiblesData.collectibles?.[0] || null,
    currentQuarter: collectiblesData.currentQuarter || 1,
    paymentsThisQuarter: collectiblesData.paymentsThisQuarter || 0,
    paymentsNeeded: collectiblesData.paymentsNeeded || 3,
    planType: collectiblesData.planType,
  }
}

interface Props {
  block: CollectibleProgress
}

export const CollectibleProgressBlock: React.FC<Props> = observer(({ block }) => {
  const { userStore } = useStores()
  const hasMounted = useDidFinishSSR()

  const { data, loading } = useQuery(
    collectibleProgressQuery,
    { collectibleId: block.collectible.id },
    mapCollectibleData
  )

  if (!hasMounted || !userStore || loading) {
    return (
      <Section ai="center" jc="center" bg="$bgPrimary">
        <Spinner size="large" />
      </Section>
    )
  }

  if (!data?.collectible) {
    return null
  }

  const { collectible, paymentsThisQuarter, paymentsNeeded, isEligibleForCollectibles } = data
  const isMonthlyPlan = data.planType?.toLowerCase().includes('monthly')

  return (
    <Section ai="flex-start" jc="flex-start" bg="$bgPrimary">
      <CompactCollectibleProgress
        title={collectible.title}
        description={collectible.description}
        quarter={collectible.quarter}
        image={collectible.image}
        current={paymentsThisQuarter}
        total={paymentsNeeded}
        unlockDate={collectible.unlockDate}
        status={collectible.status}
        isPurchasable={collectible.isPurchasable}
        isEligibleForCollectibles={isEligibleForCollectibles}
        shopifyUrl={collectible.shopifyUrl}
        redeemedDate={collectible.redeemedDate}
        isMonthlyPlan={isMonthlyPlan}
      />
    </Section>
  )
})
